import { getLeaseDuration } from '../page-templates/voucher/voucher-verify-utils';

// Single source of truth for field naming across the display, BE, and bounds vocabularies.
const FIELD_ALIASES = [
  { id: 'tenant_rep_company', beName: 'tenant_represented_by' },
  { id: 'landlord_rep_company', beName: 'landlord_represented_by' },
  { id: 'base_rent', beName: 'base_rent' },
  { id: 'end_date', beName: 'lease_expiration_date', boundsKey: 'expiration_date' },
  { id: 'floor_number', beName: 'floor' },
  { id: 'space_total_square_feet', beName: 'space_total_square_feet' },
  { id: 'building_total_square_feet', beName: 'building_total_square_feet' },
  { id: 'free_rent', beName: 'free_rent' },
  // Both aliases resolve to the same merged 'lease_escalation' checklist/preview field;
  // boundsKey keeps each sub-metric's own OOB bounds resolvable (see getOutlierFieldIds).
  { id: 'lease_escalation', beName: 'rent_escalation_dollar', boundsKey: 'lease_escalation_amount' },
  { id: 'lease_escalation', beName: 'rent_escalation_percent', boundsKey: 'lease_escalation_percent' },
  { id: 'occupancy_date', beName: 'date_lease_occupancy' },
  { id: 'operating_expenses', beName: 'operating_expenses_amount_per_sqft' },
  { id: 'lease_execution_date', beName: 'date_lease_execution', boundsKey: 'sign_date' },
  { id: 'start_date', beName: 'date_lease_from' },
  { id: 'tenant_improvement_allowance', beName: 'tenant_improvement_allowance_amount_per_sqft' },
  { id: 'term', beName: 'lease_term_months' },
  // Must match verification_field_config.get_field_source_mapping() keys, or the
  // backend silently drops the field from the verification response.
  { id: 'is_fully_executed_lease', beName: 'fully_executed_lease' },
  { id: 'has_commission_agreement', beName: 'commission_agreement' },
];

// Display field IDs to BE field names for the deal_field_layout payload.
const FIELD_ID_TO_BE_NAME = FIELD_ALIASES.reduce((acc, field) => {
  acc[field.id] = field.beName;
  return acc;
}, {});

// Any known alias (BE name or bounds key) back to the display field ID.
const API_KEY_TO_FIELD_ID = FIELD_ALIASES.reduce((acc, field) => {
  acc[field.beName] = field.id;
  if (field.boundsKey) acc[field.boundsKey] = field.id;
  return acc;
}, {});

// Every alias resolves to the same lookup list, so old and new bounds keys find
// the value stored under the BE name in the value map.
const BOUNDS_KEY_ALIASES = FIELD_ALIASES.reduce((acc, field) => {
  const aliases = [...new Set([field.beName, field.boundsKey || field.id, field.id])];
  aliases.forEach((alias) => { acc[alias] = aliases; });
  return acc;
}, {});

// Maps API fieldBounds keys to raw values from deal/comp data.
const buildValueMapFromData = (comp, info, voucher) => ({
  base_rent: comp.base_rent,
  free_rent: comp.free_rent,
  tenant_improvement_allowance_amount_per_sqft: comp.tenant_improvement_allowance_amount_per_sqft,
  date_lease_from: info.date_lease_from,
  lease_expiration_date: voucher.lease_expiration_date,
  date_lease_execution: info.date_lease_execution,
  date_lease_occupancy: info.date_lease_occupancy,
  lease_term_months: getLeaseDuration(info.date_lease_from, voucher?.lease_expiration_date),
  rent_escalation_dollar: comp.rent_escalation_dollar,
  rent_escalation_percent: comp.rent_escalation_percent,
  operating_expenses_amount_per_sqft: comp.operating_expenses_amount_per_sqft,
});

const getBoundValue = (valueMap, key) => {
  const aliases = BOUNDS_KEY_ALIASES[key] || [key];
  const alias = aliases.find(name => valueMap[name] != null);
  return alias === undefined ? null : valueMap[alias];
};

// Returns true when `date` violates the constraint expressed by `operator` against `refDate`.
const evaluateDateReference = (date, refDate, operator) => {
  switch (operator) {
    case 'LT': return date >= refDate;
    case 'LTE': return date > refDate;
    case 'GT': return date <= refDate;
    case 'GTE': return date < refDate;
    default: return false;
  }
};

// Missing means null/undefined/blank text; 0 is a real, entered value.
const isBlank = value => value == null || (typeof value === 'string' && value.trim() === '');

const isOutsideNumericRange = (rawValue, entry) => {
  const val = parseFloat(rawValue);
  return !Number.isNaN(val) && (val < parseFloat(entry.min) || val > parseFloat(entry.max));
};

// Bounds that apply to each property/space rather than to one deal-level value.
const SPACE_BOUND_KEYS = new Set(['floor_number', 'total_leased_area']);

// Returns a Set of display field IDs whose current values are missing or violate the API bounds.
// A blank value is missing for every bounded field (0 is a real value), except the optional Floor
// and Operating Expenses, which only count once something out of range was entered. Floor and
// leased area are checked on every suite that exists; a deal with no property has nothing to check.
const getOutlierFieldIds = (bounds, comp, info, voucher) => {
  const outliers = new Set();
  const valueMap = buildValueMapFromData(comp, info, voucher);

  // baseRent is a separate top-level section (always NUMERIC)
  if (bounds?.baseRent) {
    const raw = valueMap.base_rent;
    if (isBlank(raw) || isOutsideNumericRange(raw, bounds.baseRent)) outliers.add('base_rent');
  }

  Object.entries(bounds?.fieldBounds || {}).forEach(([apiKey, entry]) => {
    // Lease Escalation is a single merged field (Fixed/Custom) — handled separately below,
    // since only one of dollar/percent is active in Fixed mode and Custom has no single
    // numeric value to compare against either bound.
    if (apiKey === 'lease_escalation_amount' || apiKey === 'lease_escalation_percent') return;
    if (SPACE_BOUND_KEYS.has(apiKey)) return;
    // A bound with no known value source can't be evaluated, so it must not count as missing.
    if (!BOUNDS_KEY_ALIASES[apiKey]) return;

    const fieldId = API_KEY_TO_FIELD_ID[apiKey] || apiKey;
    const rawValue = getBoundValue(valueMap, apiKey);

    if (fieldId === 'operating_expenses' && isBlank(rawValue)) return;

    if (isBlank(rawValue)) {
      outliers.add(fieldId);
      return;
    }

    if (entry.category === 'NUMERIC') {
      if (isOutsideNumericRange(rawValue, entry)) outliers.add(fieldId);
    } else if (entry.category === 'DATE') {
      const date = new Date(rawValue);
      if (!Number.isNaN(date.getTime())) {
        let violated = false;
        if (entry.minDate) violated = date < new Date(entry.minDate);
        if (!violated && entry.maxDate) violated = date > new Date(entry.maxDate);
        if (!violated && entry.minDateReference) {
          const { attributeKey, operator } = entry.minDateReference;
          const refRaw = getBoundValue(valueMap, attributeKey);
          if (refRaw != null) {
            const refDate = new Date(refRaw);
            if (!Number.isNaN(refDate.getTime())) violated = evaluateDateReference(date, refDate, operator);
          }
        }
        if (violated) outliers.add(fieldId);
      }
    }
  });

  // Lease Escalation: compare whichever value is active (dollar vs percent) in Fixed mode
  // against its own bounds. Custom mode has no single numeric value, so bounds don't apply.
  if (comp?.rent_escalation_is_custom !== true) {
    const escalationApiKey = comp?.rent_escalation_is_percent ? 'lease_escalation_percent' : 'lease_escalation_amount';
    const escalationEntry = bounds?.fieldBounds?.[escalationApiKey];
    if (escalationEntry?.category === 'NUMERIC') {
      const rawValue = getBoundValue(valueMap, escalationApiKey);
      if (isBlank(rawValue) || isOutsideNumericRange(rawValue, escalationEntry)) outliers.add('lease_escalation');
    }
  }

  const properties = info.voucher_commercial_properties || [];
  const flagIfAnyOutlier = (key, values, { ignoreBlank = false, fieldId = key } = {}) => {
    const entry = bounds?.fieldBounds?.[key];
    if (entry?.category !== 'NUMERIC') return;
    const isOutlier = value => (isBlank(value) ? !ignoreBlank : isOutsideNumericRange(value, entry));
    if (values.some(isOutlier)) outliers.add(fieldId);
  };
  flagIfAnyOutlier(
    'floor_number',
    properties.filter(property => !property.whole_building).flatMap(property => (property.spaces || []).map(space => space.floor)),
    { ignoreBlank: true },
  );
  flagIfAnyOutlier(
    'total_leased_area',
    properties.filter(property => !property.whole_building)
      .flatMap(property => (property.spaces || []).map(space => space.space_total_square_feet)),
    { fieldId: 'space_total_square_feet' },
  );
  flagIfAnyOutlier(
    'total_leased_area',
    properties.filter(property => property.whole_building)
      .map(property => property.building_total_square_feet),
    { fieldId: 'building_total_square_feet' },
  );

  return outliers;
};

// Adapts the Review page's Redux slices to the (comp, info, voucher) shape the rule expects
// (the shape `deal_overview` returns for the Voucher Verification tab).
const buildOutlierInputsFromState = ({
  deal, voucher, compData, commercialProperties,
}) => ({
  comp: compData || {},
  info: { ...(deal || {}), voucher_commercial_properties: commercialProperties || [] },
  voucher: voucher || {},
});

export {
  FIELD_ID_TO_BE_NAME,
  buildOutlierInputsFromState,
  getLeaseDuration,
  getOutlierFieldIds,
};
